import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, apiError, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Contact, Search, Mail, Phone, MapPin, Tag, X, Download, Upload } from "lucide-react";
import { downloadTemplate as downloadTemplate_, parseSheet } from "@/lib/sheet";
import { findDuplicateRows, normVat } from "@/lib/dupes";
import { ConfirmUpdateDialog } from "@/components/ConfirmUpdateDialog";
import { t, tf } from "@/lib/i18n";

const empty = { name: "", vat: "", address: "", payment_terms: "", email: "", phone: "", discount: 0, price_overrides: {} };
const CUSTOMER_COLUMNS = [
  { key: "name", label: "Ragione sociale", width: 30 },
  { key: "vat", label: "P.IVA / Codice fiscale", width: 22 },
  { key: "address", label: "Indirizzo", width: 30 },
  { key: "payment_terms", label: "Condizioni di pagamento", width: 24 },
  { key: "email", label: "Email", width: 26 },
  { key: "phone", label: "Telefono" },
  { key: "discount", label: "Sconto %", type: "number" },
];

export default function Customers() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [ovProduct, setOvProduct] = useState("");
  const [ovPrice, setOvPrice] = useState("");

  const { data: customers = [], isLoading } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? (await api.put(`/customers/${editing.customer_id}`, payload)).data
              : (await api.post("/customers", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); toast.success(editing ? "Cliente aggiornato" : "Cliente creato"); setOpen(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/customers/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); toast.success(t("Cliente eliminato")); },
  });
  const [pendingCust, setPendingCust] = useState(null);
  const bulk = useMutation({
    mutationFn: async (rows) => (await api.post("/customers/bulk", { customers: rows })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success(tf(d.updated ? "{n} clienti importati, {u} aggiornati" : "{n} clienti importati", { n: d.inserted, u: d.updated }));
      setPendingCust(null);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const downloadTemplate = () => {
    downloadTemplate_(CUSTOMER_COLUMNS, ["Osteria Bella S.r.l.", "IT01234560220", "Via Roma 1, Trento", "30 gg d.f.", "info@osteria.it", "0461000000", 5], "modello_clienti.xlsx", "Clienti");
    toast.success(t("Modello Excel scaricato"));
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const rows = (await parseSheet(file, CUSTOMER_COLUMNS)).filter((r) => r.name).map((r) => ({ ...r, price_overrides: {} }));
      if (!rows.length) { toast.error(t("Nessuna riga valida (serve almeno la ragione sociale)")); return; }
      const dups = findDuplicateRows(rows, (r) => normVat(r.vat));
      if (dups.length) {
        toast.error(tf("P.IVA duplicate nel file, correggi le righe: {list}", { list: dups.slice(0, 8).join("; ") }), { duration: 10000 });
        return;
      }
      const existingVats = new Set(customers.map((c) => normVat(c.vat)).filter(Boolean));
      const updates = rows.filter((r) => existingVats.has(normVat(r.vat))).length;
      if (updates) setPendingCust({ rows, updates });
      else bulk.mutate(rows);
    } catch {
      toast.error(t("File non leggibile. Usa il modello Excel o un CSV."));
    }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, price_overrides: c.price_overrides || {} }); setOpen(true); };
  const str = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addOverride = () => {
    if (!ovProduct || ovPrice === "") return;
    setForm((f) => ({ ...f, price_overrides: { ...f.price_overrides, [ovProduct]: Number(ovPrice) } }));
    setOvProduct(""); setOvPrice("");
  };
  const removeOverride = (pid) => setForm((f) => { const n = { ...f.price_overrides }; delete n[pid]; return { ...f, price_overrides: n }; });
  const productName = (pid) => products.find((p) => p.product_id === pid)?.name || pid;

  const filtered = customers.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || (c.vat || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <ConfirmUpdateDialog pending={pendingCust} onCancel={() => setPendingCust(null)} onConfirm={() => bulk.mutate(pendingCust.rows)} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Rubrica clienti")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{customers.length} {t("clienti · listini personalizzati")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-customer-template-btn"><Download className="h-4 w-4" /> {t("Modello Excel")}</Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="import-customers-btn"><Upload className="h-4 w-4" /> {t("Importa")}</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-customer-file-input" />
          <Button onClick={openNew} className="gap-2 font-semibold" data-testid="add-customer-btn"><Plus className="h-4 w-4" /> {t("Nuovo cliente")}</Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Cerca per nome o P.IVA")} className="pl-9" />
      </div>

      {isLoading ? <p className="text-muted-foreground">{t("Caricamento…")}</p> : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center">
          <Contact className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("Nessun cliente. I clienti salvati sono riutilizzabili negli ordini.")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c, i) => (
            <motion.div key={c.customer_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }} data-testid={`customer-card-${c.customer_id}`} className="bg-white border border-border rounded-xl p-5 hover:border-primary/40 transition-all space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><p className="font-semibold truncate">{c.name}</p>{c.vat && <p className="text-xs font-mono-j text-muted-foreground">{c.vat}</p>}</div>
                {c.discount > 0 && <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full font-semibold whitespace-nowrap flex items-center gap-1"><Tag className="h-3 w-3" /> -{c.discount}%</span>}
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                {c.address && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {c.address}</p>}
                {c.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {c.email}</p>}
                {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {c.phone}</p>}
                {c.price_overrides && Object.keys(c.price_overrides).length > 0 && (
                  <p className="text-xs text-primary font-medium">{Object.keys(c.price_overrides).length} {t("prezzi dedicati")}</p>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.customer_id}`}><Pencil className="h-3.5 w-3.5" /> {t("Modifica")}</Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove.mutate(c.customer_id)} data-testid={`delete-customer-${c.customer_id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? t("Modifica cliente") : t("Nuovo cliente")}</DialogTitle>
            <DialogDescription>{t("Dati anagrafici e listino personalizzato.")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); save.mutate({ ...form, discount: Number(form.discount) }); }} className="space-y-4">
            <div className="space-y-2"><Label>{t("Nome / Ragione sociale *")}</Label><Input data-testid="customer-form-name" required value={form.name} onChange={str("name")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>{t("P.IVA / CF")}</Label><Input data-testid="customer-form-vat" value={form.vat} onChange={str("vat")} /></div>
              <div className="space-y-2"><Label>{t("Pagamento")}</Label><Input data-testid="customer-form-payment" value={form.payment_terms} onChange={str("payment_terms")} placeholder={t("30gg")} /></div>
              <div className="space-y-2 col-span-2"><Label>{t("Indirizzo")}</Label><Input data-testid="customer-form-address" value={form.address} onChange={str("address")} /></div>
              <div className="space-y-2"><Label>{t("Email")}</Label><Input data-testid="customer-form-email" type="email" value={form.email} onChange={str("email")} /></div>
              <div className="space-y-2"><Label>{t("Telefono")}</Label><Input data-testid="customer-form-phone" value={form.phone} onChange={str("phone")} /></div>
            </div>

            <div className="rounded-lg border border-border p-3 space-y-3 bg-secondary/40">
              <div className="space-y-2"><Label className="flex items-center gap-2"><Tag className="h-4 w-4 text-primary" /> {t("Sconto dedicato (%)")}</Label>
                <Input data-testid="customer-discount-input" type="number" step="0.1" min="0" max="100" value={form.discount} onChange={str("discount")} className="max-w-[140px]" />
                <p className="text-xs text-muted-foreground">{t("Applicato a tutto il carrello di questo cliente.")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("Prezzi dedicati per prodotto")}</Label>
                {Object.entries(form.price_overrides).map(([pid, pr]) => (
                  <div key={pid} className="flex items-center gap-2 text-sm bg-white border border-border rounded-lg px-3 py-1.5">
                    <span className="flex-1 truncate">{productName(pid)}</span>
                    <span className="font-semibold text-primary">{eur(pr)}</span>
                    <button type="button" onClick={() => removeOverride(pid)} className="text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Select value={ovProduct} onValueChange={setOvProduct}>
                    <SelectTrigger className="flex-1" data-testid="override-product-select"><SelectValue placeholder={t("Prodotto")} /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder={t("Prezzo €")} value={ovPrice} onChange={(e) => setOvPrice(e.target.value)} className="w-28" data-testid="override-price-input" />
                  <Button type="button" variant="outline" onClick={addOverride} data-testid="override-add-btn"><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>

            <DialogFooter><Button type="submit" disabled={save.isPending} className="font-semibold" data-testid="save-customer-btn">{save.isPending ? t("Salvataggio…") : t("Salva")}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
